import {action, observable, runInAction} from 'mobx';
import request from '../utils/request';
import {INVITE, MANAGE_STUDENT} from '../constants/urlConstants';
import GenericFormStore from './FormStore';
import manageStore from './ManageStore';
import appStore from '../containers/app/store';
import mainStore from '../containers/main/store';
import dialogStore from '../containers/dialog/store';
import {customEvent} from '../utils/analyticsUtil';
import {actions, category, DialogTypes, events, Roles} from '../constants/enums';
import {getDefaultAvatar, isSchoolUser} from '../utils/userUtility';

class InviteMemberStore extends GenericFormStore {
    @observable emails = [];
    @observable avatar = getDefaultAvatar();
    @observable studentId = null;
    @observable isFetching = false;
    @observable isProcessing = false;
    @observable isConnectMode = false;
    @observable form = this.initForm();
    @observable activeUser = null;
    @observable disableEmailChange = false;
    @observable student = {}

    initForm = () => {
        return {
            fields: {
                name: {
                    value: '',
                    error: '',
                    rule: 'required'
                },
                familyName: {
                    value: '',
                    error: '',
                    rule: 'string'
                },
                email: {
                    value: '',
                    error: '',
                    rule: 'email'
                },
                instrument: {
                    value: '',
                    error: '',
                    rule: 'string',
                }
            },
            meta: {
                isValid: false,
                error: null
            }
        };
    };

    @action setStudent = (student) => {
        this.student = student
    }

    @action setActiveUser = (user) => {
        this.disableEmailChange = false;
        this.activeUser = user;
    };

    @action setDisabledEmailChange = (val) => {
        this.disableEmailChange = val;
    };

    @action setConnectMode = (val) => {
        this.isConnectMode = val;
    };

    @action setAvatar = (avatar) => {
        if (avatar) {
            this.avatar = avatar.indexOf('molly') >= 0 ? getDefaultAvatar() : avatar;
        } else {
            this.avatar = getDefaultAvatar();
        }
    };

    @action setChip = (chip, name) => {
        this.form.fields[name].value = chip;
        this.onChange(name, this.form.fields[name].value);
    };

    @action setStudentData = ({name, email, givenName, familyName, avatar, id, instruments}) => {
        this.setConnectMode(true);
        this.studentId = id;
        this.form.fields.name.value = givenName ? givenName : name;
        this.form.fields.familyName.value = familyName ? familyName : '';
        this.form.fields.instrument.value = instruments && instruments.length > 0 ? instruments[0] : null;
        this.form.fields.email.value = email;
        this.setAvatar(avatar);
    };

    @action sendEmail = async (email, assignedProjectId, resend = false) => {
        this.isFetching = true;
        try {
            const resp = await request(INVITE, {method: 'POST', body: {email, assignedProjectId}});
            runInAction(() => {
                this.isFetching = false;
                if (manageStore.updateList(resp.student || {email}) && !resend) {
                    //this.showUserIsAlreadyRegisteredDialog = true;
                }
            });
        } catch (err) {
            runInAction(() => {
                this.isFetching = false;
            });
        }
    };

    @action sendReminderEmail = async (id, isDefaultPassword = false) => {
        mainStore.showProgressLoader();
        this.isFetching = true;
        try {
            let result = await request(`${MANAGE_STUDENT}/${id}/resend`, {method: 'POST', body: {isDefaultPassword: isDefaultPassword}});
            if (result.success) {
                appStore.createAlertFromMessageId('success', 'notification.invite.sent.success', 2500);
                // dialogStore.openDialog(DialogTypes.ResendLogin);
            } else {
                appStore.createAlertFromMessageId('warning', 'manage.action.notsent', 2500);
                manageStore.updateStudentStatus(id, result.student.email, result.student.status, result.student.givenName, result.student.familyName);
            }
        } catch (err) {
        } finally {
            runInAction(() => {
                this.isFetching = false;
                mainStore.hideProgressLoader();
            });
        }
    };

    @action inviteStudent = async (email, name, familyName, userAvatar, instrument, disableNotification, isDemoStudent = false) => {
        mainStore.showProgressLoader();
        this.isProcessing = true;
        let response;
        try {
            if (isSchoolUser()) {
                await manageStore.fetchStudentList(true);
                // todo change this is an event for schools
                // customEvent(events.CustomEvent, {category: category.SelectAStudent, action: (email ? actions.StudentAdded : actions.StudentAddedNoEmail)});
            }
            response = await request(MANAGE_STUDENT, {method: 'POST', body: {email, name, familyName, avatar: userAvatar, instrument, isDemoStudent}});
            customEvent(events.CustomEvent, {category: category.SelectAStudent, action: (email ? actions.StudentAdded : actions.StudentAddedNoEmail)});
            let id = response?.student?.id || response?.length > 0 && response[0].user?.id;
            manageStore.updateList({
                email,
                givenName: name,
                familyName,
                id,
                avatar: userAvatar,
                instruments: instrument,
                role: Roles.Student,
            });
            this.setStudent({id: id, email, name})
            if (response?.isExisting) {
                dialogStore.openUserIsAlreadyRegisteredDialog();
            }
            return {id, name: [name, familyName].join(' ').trim(), avatar: userAvatar};
        } catch (err) {
            console.warn(err);
        } finally {
            runInAction(() => {
                dialogStore.closeDialog(DialogTypes.AddStudent);
                this.resetForm();
                mainStore.hideProgressLoader();
                this.isProcessing = false;
            });
        }
    }
    ;

    @action
    connectStudent = async (email, firstName, familyName, avatar) => {
        mainStore.showProgressLoader();
        this.isProcessing = true;
        try {
            const response = await request(`${MANAGE_STUDENT}/${this.studentId}/connect`, {method: 'POST', body: {email, firstName, familyName, avatar}});
            manageStore.updateStudentStatus(this.studentId, response.student.email, response.student.status, response.student.givenName || firstName, response.student.familyName || familyName, response.student.avatar);
            if (response.isExisting) {
                dialogStore.openUserIsAlreadyRegisteredDialog();
            }
            customEvent(events.CustomEvent, {category: category.StudentConnected});
        } catch (err) {
            console.warn(err);
        } finally {
            runInAction(() => {
                dialogStore.closeDialog(DialogTypes.AddStudent);
                this.resetForm();
                this.setConnectMode(false);
                mainStore.hideProgressLoader();
                this.isProcessing = false;
            });
        }
    };

    @action
    validateForm = () => {
        const {name, email} = this.form.fields;
        if (!name.value || name.error || email.error) {
            this.form.meta.error = 'Please fill all the fields correctly';
            this.clearMetaError();
            return false;
        }
        return true;
    };

    @action
    resetForm = () => {
        this.form = this.initForm();
        this.disableEmailChange = false;
    };

    clearMetaError = () => {
        setTimeout(() => {
            runInAction(() => {
                this.form.meta.error = '';
            });
        }, 3500);
    };
}

const
    inviteMemberStore = new InviteMemberStore();
export default inviteMemberStore;
